import React, { forwardRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { useTranslation } from '../i18nContext';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
// Default appearance is "interaction-only" → widget is invisible until/unless
// Cloudflare's risk engine asks the user to solve a challenge. Override with
// REACT_APP_TURNSTILE_APPEARANCE=always for managed (always-visible) mode.
const APPEARANCE = process.env.REACT_APP_TURNSTILE_APPEARANCE || 'interaction-only';

/**
 * CaptchaField — wraps Cloudflare Turnstile for the public CMIC forms
 * (contact, registration request, forgot password, security-question
 * recovery). Reads the site key from REACT_APP_TURNSTILE_SITE_KEY; when
 * that's empty the component renders nothing so dev/local works.
 *
 * Usage:
 *   const ref = useRef(null);
 *   const [token, setToken] = useState('');
 *   <CaptchaField ref={ref} onToken={setToken} />
 *
 *   // After a failed submit:
 *   if (ref.current) ref.current.reset();
 *
 *   // Send `captcha_token: token` in the request body.
 */
const CaptchaField = forwardRef(function CaptchaField({ onToken, className = 'mb-4' }, ref) {
  const t = useTranslation();
  if (!SITE_KEY) return null;
  return (
    <div className={className} data-testid="captcha-field">
      <Turnstile
        ref={ref}
        siteKey={SITE_KEY}
        options={{
          theme: 'light',
          language: t?.locale === 'en' ? 'en' : 'es',
          appearance: APPEARANCE,
          size: APPEARANCE === 'interaction-only' ? 'flexible' : 'normal',
        }}
        onSuccess={(token) => onToken(token)}
        onError={() => onToken('')}
        onExpire={() => onToken('')}
      />
    </div>
  );
});

// True when Turnstile is configured for this environment.
export const captchaConfigured = !!SITE_KEY;

export default CaptchaField;
